/**
 * Embeddable quiz entry: bootstraps shadow DOM, fetches or mocks CMS config, attaches scroll/exit triggers,
 * and exposes `window.ECEQuizWidget` for hosts. Auto-inits when the script tag includes `data-ece-quiz-auto`.
 */
import widgetCss from './styles/widget.css?inline';
import {
  getPathfinderAllowedOrigins,
  getPathfinderBaseUrl,
  getPathfinderPath,
  isDeliveryConfigured,
  isPathfinderConfigured,
  useMockQuiz,
} from './config/env';
import { fetchQuizForArticle } from './api/fetch-quiz';
import { getMockQuizConfig } from './api/mock-quiz';
import type { QuizConfig } from './model/types';
import { EceQuizWidgetController } from './ui/widget-controller';
import { attachModalTriggers } from './triggers/attach-triggers';
import { markModalConsumed, shouldSkipInit } from './session/modal-session';
import { resolveRsidForSession } from './session/rsid';
import {
  resolveArticleUrl,
  resolveArticleUrlOverrideFromAutoScript,
  sessionStorageKeyForArticle,
} from './utils/article-url';
import { getTrackingParamsFromUrl } from './utils/tracking';

export interface InitOptions {
  /** When omitted, uses `window.location.href` without hash (override if CMS stores a different canonical URL). */
  articleUrl?: string;
  /** Scroll depth 0–1; default 0.5 */
  scrollDepthRatio?: number;
}

/** Set after a successful async bootstrap so `init` is not run twice. */
let bootstrapped = false;
/** Prevents overlapping async `init` runs from duplicate hosts or racey callers. */
let initInFlight = false;

/**
 * Resolves quiz content, mounts the widget in a closed shadow root, and registers one-shot modal triggers.
 *
 * No-ops (no DOM, no errors) when: `document` is missing, article URL is empty, this article’s quiz was
 * already opened or the intent gate was declined in `sessionStorage`, env is incomplete, fetch/parse fails,
 * or a prior `init` completed.
 */
export function init(options: InitOptions = {}): void {
  if (bootstrapped || initInFlight || typeof document === 'undefined') return;

  const articleUrl = resolveArticleUrl(options.articleUrl);
  if (!articleUrl) return;

  const sessionKey = sessionStorageKeyForArticle(articleUrl);
  if (shouldSkipInit(sessionKey)) return;

  const mock = useMockQuiz();
  if (
    import.meta.env.DEV &&
    mock &&
    (import.meta.env.VITE_DEV_ARTICLE_URL ?? '').trim()
  ) {
    console.warn(
      '[ece-quiz] VITE_USE_MOCK_QUIZ=true skips the Delivery API — quiz copy comes from mock-quiz.ts. Set VITE_USE_MOCK_QUIZ=false to load from CMS using VITE_DEV_ARTICLE_URL.',
    );
  }
  if (!mock && !isDeliveryConfigured()) return;
  if (!mock && !isPathfinderConfigured()) return;
  if (mock && !isPathfinderConfigured()) return;

  initInFlight = true;
  void (async () => {
    try {
      const config: QuizConfig | null = mock ? getMockQuizConfig(articleUrl) : await fetchQuizForArticle(articleUrl);
      if (!config) return;

      const host = document.createElement('div');
      host.id = 'ece-quiz-widget-host';
      document.body.appendChild(host);
      const shadow = host.attachShadow({ mode: 'open' });

      const styleEl = document.createElement('style');
      styleEl.textContent = widgetCss;
      shadow.appendChild(styleEl);

      const mount = document.createElement('div');
      shadow.appendChild(mount);

      const tracking = getTrackingParamsFromUrl();
      const rsid = resolveRsidForSession();

      const controller = new EceQuizWidgetController({
        config,
        mount,
        sessionKey,
        pathfinderBaseUrl: getPathfinderBaseUrl(),
        pathfinderPath: getPathfinderPath(),
        pathfinderAllowedOrigins: getPathfinderAllowedOrigins(),
        tracking,
        rsid,
      });

      const scrollDepthRatio =
        typeof options.scrollDepthRatio === 'number' &&
        options.scrollDepthRatio >= 0 &&
        options.scrollDepthRatio <= 1
          ? options.scrollDepthRatio
          : 0.5;

      attachModalTriggers({
        scrollDepthRatio,
        isSuppressed: () => shouldSkipInit(sessionKey),
        onFire: (detail) => {
          controller.showIntentGate(detail);
        },
      });

      bootstrapped = true;
    } finally {
      initInFlight = false;
    }
  })();
}

/** Built IIFE assigns this global (`vite.config` `lib.name`). */
declare global {
  interface Window {
    ECEQuizWidget?: { init: typeof init; markModalConsumed: typeof markModalConsumed };
  }
}

if (typeof window !== 'undefined') {
  window.ECEQuizWidget = { init, markModalConsumed };
}

const cur = document.currentScript;
if (cur instanceof HTMLScriptElement && cur.hasAttribute('data-ece-quiz-auto')) {
  init({ articleUrl: resolveArticleUrlOverrideFromAutoScript(cur) });
}
